/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * FastAPI
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { mutationOptionsWithInteraction } from '../../mutationOptions'
import { customInstance } from '../../mutator'
import type {
  HTTPValidationError,
  OrganisationConstructionCompaniesOut,
  OrganisationConstructionCompanyCreateDTO,
  OrganisationConstructionCompanyDTO,
  ParticipantMembersOut,
} from '../fastAPI.schemas'

/**
 * The active organisation's own Auftraggeberinnen (R18).

Only that list -- the legacy global rows remain an export fallback and
are deliberately not offered here.
 * @summary Get Organisation Construction Companies
 */
export const getOrganisationConstructionCompanies = (signal?: AbortSignal) => {
  return customInstance<OrganisationConstructionCompaniesOut>({
    url: `/participants/construction-companies`,
    method: 'GET',
    signal,
  })
}

export const getGetOrganisationConstructionCompaniesQueryKey = () => {
  return [`/participants/construction-companies`] as const
}

export const getGetOrganisationConstructionCompaniesQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>,
    TError,
    TData
  >
}) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetOrganisationConstructionCompaniesQueryKey()

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>
  > = ({ signal }) => getOrganisationConstructionCompanies(signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetOrganisationConstructionCompaniesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>
>
export type GetOrganisationConstructionCompaniesQueryError = HTTPValidationError

/**
 * @summary Get Organisation Construction Companies
 */

export function useGetOrganisationConstructionCompanies<
  TData = Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationConstructionCompanies>>,
    TError,
    TData
  >
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOrganisationConstructionCompaniesQueryOptions(options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Add an Auftraggeberin to the active organisation's list.

Any member may -- the list is project data, not the admin-gated library.
The duplicate check is scoped to the organisation: another company having
the same client is no conflict (R18).
 * @summary Create Organisation Construction Company
 */
export const createOrganisationConstructionCompany = (
  organisationConstructionCompanyCreateDTO: OrganisationConstructionCompanyCreateDTO,
  signal?: AbortSignal,
) => {
  return customInstance<OrganisationConstructionCompanyDTO>({
    url: `/participants/construction-companies`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: organisationConstructionCompanyCreateDTO,
    signal,
  })
}

export const useCreateOrganisationConstructionCompanyMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganisationConstructionCompany>>,
    TError,
    { data: OrganisationConstructionCompanyCreateDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrganisationConstructionCompany>>,
  TError,
  { data: OrganisationConstructionCompanyCreateDTO },
  TContext
> => {
  const mutationKey = ['createOrganisationConstructionCompany']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrganisationConstructionCompany>>,
    { data: OrganisationConstructionCompanyCreateDTO }
  > = props => {
    const { data } = props ?? {}

    return createOrganisationConstructionCompany(data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/participants/construction-companies` },
    {
      operationId:
        'create_organisation_construction_company_participants_construction_companies_post',
      operationName: 'createOrganisationConstructionCompany',
    },
  )

  return customOptions
}

export type CreateOrganisationConstructionCompanyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrganisationConstructionCompany>>
>
export type CreateOrganisationConstructionCompanyMutationBody =
  OrganisationConstructionCompanyCreateDTO
export type CreateOrganisationConstructionCompanyMutationError = HTTPValidationError

/**
 * @summary Create Organisation Construction Company
 */
export const useCreateOrganisationConstructionCompany = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganisationConstructionCompany>>,
    TError,
    { data: OrganisationConstructionCompanyCreateDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof createOrganisationConstructionCompany>>,
  TError,
  { data: OrganisationConstructionCompanyCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateOrganisationConstructionCompanyMutationOptions(options)

  return useMutation(mutationOptions)
}
/**
 * Every member of the active organisation, with their eligibility.

One list for both people pickers: Bearbeitung offers everyone,
Planungsverantwortung filters on ``is_responsible`` (R17).
 * @summary Get Participant Members
 */
export const getParticipantMembers = (signal?: AbortSignal) => {
  return customInstance<ParticipantMembersOut>({
    url: `/participants/members`,
    method: 'GET',
    signal,
  })
}

export const getGetParticipantMembersQueryKey = () => {
  return [`/participants/members`] as const
}

export const getGetParticipantMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof getParticipantMembers>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getParticipantMembers>>, TError, TData>
}) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetParticipantMembersQueryKey()

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getParticipantMembers>>> = ({ signal }) =>
    getParticipantMembers(signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getParticipantMembers>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetParticipantMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof getParticipantMembers>>
>
export type GetParticipantMembersQueryError = HTTPValidationError

/**
 * @summary Get Participant Members
 */

export function useGetParticipantMembers<
  TData = Awaited<ReturnType<typeof getParticipantMembers>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getParticipantMembers>>, TError, TData>
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetParticipantMembersQueryOptions(options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * The owning organisation's Auftraggeberinnen, for editing this project.

Follows the project's own organisation rather than the header, like
``GET /projects/{guid}/members`` (R18).
 * @summary Get Project Construction Companies
 */
export const getProjectConstructionCompanies = (projectGuid: string, signal?: AbortSignal) => {
  return customInstance<OrganisationConstructionCompaniesOut>({
    url: `/projects/${projectGuid}/construction-companies`,
    method: 'GET',
    signal,
  })
}

export const getGetProjectConstructionCompaniesQueryKey = (projectGuid?: string) => {
  return [`/projects/${projectGuid}/construction-companies`] as const
}

export const getGetProjectConstructionCompaniesQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectConstructionCompanies>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProjectConstructionCompanies>>,
      TError,
      TData
    >
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetProjectConstructionCompaniesQueryKey(projectGuid)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectConstructionCompanies>>> = ({
    signal,
  }) => getProjectConstructionCompanies(projectGuid, signal)

  return { queryKey, queryFn, enabled: !!projectGuid, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjectConstructionCompanies>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetProjectConstructionCompaniesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProjectConstructionCompanies>>
>
export type GetProjectConstructionCompaniesQueryError = HTTPValidationError

/**
 * @summary Get Project Construction Companies
 */

export function useGetProjectConstructionCompanies<
  TData = Awaited<ReturnType<typeof getProjectConstructionCompanies>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProjectConstructionCompanies>>,
      TError,
      TData
    >
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetProjectConstructionCompaniesQueryOptions(projectGuid, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Add an Auftraggeberin to the owning organisation's list while editing
this project -- never to the active organisation's, which may differ.
 * @summary Create Project Construction Company
 */
export const createProjectConstructionCompany = (
  projectGuid: string,
  organisationConstructionCompanyCreateDTO: OrganisationConstructionCompanyCreateDTO,
  signal?: AbortSignal,
) => {
  return customInstance<OrganisationConstructionCompanyDTO>({
    url: `/projects/${projectGuid}/construction-companies`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: organisationConstructionCompanyCreateDTO,
    signal,
  })
}

export const useCreateProjectConstructionCompanyMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProjectConstructionCompany>>,
    TError,
    { projectGuid: string; data: OrganisationConstructionCompanyCreateDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProjectConstructionCompany>>,
  TError,
  { projectGuid: string; data: OrganisationConstructionCompanyCreateDTO },
  TContext
> => {
  const mutationKey = ['createProjectConstructionCompany']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProjectConstructionCompany>>,
    { projectGuid: string; data: OrganisationConstructionCompanyCreateDTO }
  > = props => {
    const { projectGuid, data } = props ?? {}

    return createProjectConstructionCompany(projectGuid, data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/projects/{projectGuid}/construction-companies` },
    {
      operationId:
        'create_project_construction_company_projects__project_guid__construction_companies_post',
      operationName: 'createProjectConstructionCompany',
    },
  )

  return customOptions
}

export type CreateProjectConstructionCompanyMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProjectConstructionCompany>>
>
export type CreateProjectConstructionCompanyMutationBody = OrganisationConstructionCompanyCreateDTO
export type CreateProjectConstructionCompanyMutationError = HTTPValidationError

/**
 * @summary Create Project Construction Company
 */
export const useCreateProjectConstructionCompany = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProjectConstructionCompany>>,
    TError,
    { projectGuid: string; data: OrganisationConstructionCompanyCreateDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof createProjectConstructionCompany>>,
  TError,
  { projectGuid: string; data: OrganisationConstructionCompanyCreateDTO },
  TContext
> => {
  const mutationOptions = useCreateProjectConstructionCompanyMutationOptions(options)

  return useMutation(mutationOptions)
}
